import BaseEntity from './BaseEntity';

import { EEntityType, IRotatedBoxPure } from '../types';
import { EEntityStatus, ETransformType } from '../types/entity';
import { isBoolean, isNumber } from 'lodash-es';
import TransformHelper from './2d/transformHelper/transformHelper';
import { Point2dUtils } from '../math/Point2dUtils';
import { EEnableScaleMode, ITransformEntityParams } from './type';
import { BoxesOutlineModel } from './2d/boxesOutlineModel';
import { selectedOutlineColor } from '../constant/entity';
import { IPoint2d } from '../math/IPoint2d';

export default class TransformEntity extends BaseEntity<EEntityType.Transform> {
  private _center: IPoint2d = { x: 0, y: 0 };
  private _width: number = 0;
  private _height: number = 0;
  private _rotation: number = 0;
  protected override _model2d: TransformHelper;
  private _boxesOutlineModel: BoxesOutlineModel;
  private _status: EEntityStatus = EEntityStatus.normal;
  private _transformType: ETransformType = ETransformType.Empty;
  private _enableScaleMode: EEnableScaleMode = EEnableScaleMode.Empty;
  private _enableRotation: boolean = true;
  private _boxes: IRotatedBoxPure[] = [];

  private _created: boolean = false;

  constructor() {
    super({ type: EEntityType.Transform });
    this._model2d = new TransformHelper();
    this._boxesOutlineModel = new BoxesOutlineModel();
  }

  get created() {
    return this._created;
  }

  getData() {
    return {
      center: this._center,
      width: this._width,
      height: this._height,
      rotation: this._rotation
    };
  }

  get model2d() {
    return this._model2d;
  }

  override update({
    center,
    width,
    height,
    rotation,
    status,
    transformType,
    forceUpdate,
    enableScaleMode,
    enableRotation,
    boxes
  }: Partial<ITransformEntityParams>) {
    let notDestroyUpdate = false;
    let needDestroyUpdate = forceUpdate;

    if (center && !Point2dUtils.equals(center, this._center)) {
      this._center = center;
      notDestroyUpdate = true;
    }
    if (isNumber(width) && width !== this._width) {
      this._width = width;
      needDestroyUpdate = true;
    }
    if (isNumber(height) && height !== this._height) {
      this._height = height;
      needDestroyUpdate = true;
    }
    if (isNumber(rotation) && rotation !== this._rotation) {
      this._rotation = rotation;
      notDestroyUpdate = true;
    }
    if (status && status !== this._status) {
      this._status = status;
      notDestroyUpdate = true;
    }
    if (transformType && transformType !== this._transformType) {
      this._transformType = transformType;
      notDestroyUpdate = true;
    }
    if (enableScaleMode && enableScaleMode !== this._enableScaleMode) {
      this._enableScaleMode = enableScaleMode;
      needDestroyUpdate = true;
    }
    if (isBoolean(enableRotation) && enableRotation !== this._enableRotation) {
      this._enableRotation = enableRotation;
      needDestroyUpdate = true;
    }

    if (needDestroyUpdate) {
      this._model2d.update({
        center: this._center,
        width: this._width,
        height: this._height,
        rotation: this._rotation,
        status: this._status,
        transformType: this._transformType,
        enableScaleMode: this._enableScaleMode,
        enableRotation: this._enableRotation
      });
      this._boxes = boxes && boxes.length > 1 ? boxes.slice(0, boxes.length - 1) : [];
      this._created = true;
    } else if (notDestroyUpdate) {
      this._model2d.updateWithoutDestroy({
        center: this._center,
        rotation: this._rotation,
        status: this._status,
        transformType: this._transformType
      });
      // TODO: updateWithoutDestroy 也会 createModel 导致混乱的 created 状态，后续需要优化
      this._created = true;
    }
    this.createBoxesOutline();
  }

  /** @todo 需要优化创建更新逻辑 */
  createBoxesOutline() {
    this._boxesOutlineModel.clear();
    this._boxesOutlineModel.createModel({
      boxes: this._boxes,
      linewidth: 1,
      color: selectedOutlineColor,
      x: this._center.x,
      y: this._center.y,
      z: 0,
      rotation: this._rotation
    });
  }

  clear() {
    this._model2d.clear();
    this._boxesOutlineModel.clear();
    this._created = false;
  }
}
